import * as am4charts from '@amcharts/amcharts4/charts';
import * as am4core from '@amcharts/amcharts4/core';
import { color } from '@amcharts/amcharts4/core';
import {
  setupBaseChart,
  setupCategoryAxisBase,
  setupLegendBase,
  setupMarkerColumnBase,
  setupSeriesTooltipBase,
  setupValueAxisBase,
} from 'features/shared/ui/charts';

const grey = color('#091526');

export interface ChartData {
  value: number;
  parallel: string;
}

export interface Series {
  title: string;
  data: ChartData[];
  color: string;
}

const setupLegend = () => {
  const legend = setupLegendBase(true);

  legend.paddingLeft = -25;
  legend.position = 'bottom';
  legend.dy = 14;
  legend.itemContainers.template.paddingLeft = 20;

  const marker = legend.markers.template;

  setupMarkerColumnBase(marker);

  return legend;
};

function setupCategoryAxis(categoryAxis: am4charts.CategoryAxis, isClustered: boolean) {
  setupCategoryAxisBase(categoryAxis);

  categoryAxis.dataFields.category = 'parallel';
  categoryAxis.paddingTop = -4;

  if (isClustered) {
    categoryAxis.renderer.cellStartLocation = 0.2;
    categoryAxis.renderer.cellEndLocation = 0.8;
  }
}

function setupValueAxis(valueAxis: am4charts.ValueAxis) {
  setupValueAxisBase(valueAxis);
  valueAxis.extraMax = 0.25;
}

function setupSeries(series: am4charts.ColumnSeries, name: string, data: ChartData[], isClustered: boolean) {
  setupSeriesTooltipBase(series);

  series.dataFields.categoryX = 'parallel';
  series.name = name;
  series.data = data;
  series.clustered = isClustered;
  series.dataFields.valueY = 'value';

  if (!isClustered) {
    let bullet = series.bullets.push(new am4charts.LabelBullet());

    bullet.label.text = '{value}';
    bullet.fill = grey;
    bullet.fillOpacity = 0.85;
    bullet.dy = -16;

    series.columns.template.width = am4core.percent(52);
  } else {
    series.columns.template.width = am4core.percent(75);
  }

  series.columns.template.tooltipText = '{valueY}';
}

function createSeries(chart: am4charts.XYChart, name: string, data: ChartData[], isClustered: boolean) {
  const series = chart.series.push(new am4charts.ColumnSeries());
  setupSeries(series, name, data, isClustered);
}

export function setupChart(element: HTMLDivElement, series: Series[], isClustered: boolean) {
  const chart = am4core.create(element, am4charts.XYChart);
  const colors = series.map((s) => color(s.color));

  setupBaseChart(chart);

  chart.paddingLeft = -10;
  chart.paddingBottom = 20;

  let categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
  setupCategoryAxis(categoryAxis, isClustered);

  let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
  setupValueAxis(valueAxis);

  chart.colors.list = colors;
  chart.data = series.reduce((prev: ChartData[], curr) => prev.concat(curr.data), []);

  series.forEach((s) => {
    createSeries(chart, `— ${s.title}`, s.data, isClustered);
  });

  chart.legend = setupLegend();
  return chart;
}
